import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@atrvd/saas-ui'
import { buildPageNumbers } from './buildPageNumbers'

/**
 * Reusable pagination control with page numbers and ellipsis.
 * Used by import detail modals to navigate paginated data.
 *
 * @param page - Current 0-based page index
 * @param totalPages - Total number of pages
 * @param onPageChange - Callback when user clicks a page number or arrow
 */
export function PaginationControls({
  page,
  totalPages,
  onPageChange,
}: {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}) {
  if (totalPages <= 1) return null

  const pages = buildPageNumbers(totalPages, page)

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        Page {page + 1} of {totalPages}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
          className="h-8 w-8 p-0"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {pages.map((p, idx) =>
          p === '...' ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-1 text-sm text-muted-foreground"
              // aria-hidden — the ellipsis is purely visual filler between
              // page-number anchors; screen readers should skip it rather
              // than literally read "dot dot dot" each time they cross it.
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(p)}
              className="h-8 w-8 p-0 text-xs"
              // Visible label is just the 1-based page number ("3"), which a
              // screen reader announces as a bare digit with no context. The
              // aria-label spells out the action AND the current-page state
              // so the user hears "Go to page 3" / "Page 3, current page".
              // `aria-current="page"` is the WAI-ARIA-blessed signal for the
              // active item in a paginated set (better than depending on
              // SR-specific suffix wording).
              aria-label={
                p === page ? `Page ${p + 1}, current page` : `Go to page ${p + 1}`
              }
              aria-current={p === page ? 'page' : undefined}
            >
              {p + 1}
            </Button>
          ),
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(page + 1)}
          className="h-8 w-8 p-0"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
